import { Building2, Loader2 } from 'lucide-react';
import type { TenantInfo } from '../lib/api';
import { applyBranding } from './branding';

interface TenantPickerProps {
  tenants: TenantInfo[];
  loading?: boolean;
  /** Persist + apply branding, then enter the tenant (reload to rebind Firestore). */
  onSelect?: (code: string) => void;
}

/**
 * Full-screen card list shown when an agent belongs to more than one tenant and
 * none is selected yet. Clicking a card persists the selection, applies the
 * brand color, and reloads so Firestore rebinds to the tenant's named DB.
 */
export function TenantPicker({ tenants, loading, onSelect }: TenantPickerProps) {
  function choose(t: TenantInfo) {
    applyBranding(t);
    if (onSelect) {
      onSelect(t.code);
    } else {
      window.location.reload();
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-gray-900">Choose a workspace</h1>
          <p className="mt-1 text-sm text-gray-500">
            You have access to more than one tenant. Pick one to continue.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <ul className="space-y-3">
            {tenants.map((t) => {
              const color = t.primaryColor || '#2563eb';
              return (
                <li key={t.code}>
                  <button
                    type="button"
                    onClick={() => choose(t)}
                    className="w-full flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1"
                    style={{ outlineColor: color }}
                  >
                    <span
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-md text-white"
                      style={{ backgroundColor: color }}
                    >
                      {t.logoUrl ? (
                        <img src={t.logoUrl} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Building2 className="h-5 w-5" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900">
                        {t.name || t.code}
                      </span>
                      <span className="block truncate text-xs text-gray-500">{t.code}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
